import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MODULE_NAMES, ModuleCode, usePermissions } from "../context/PermissionContext";
import { auditService, companyProfileService, settingsService } from "../services/dataService";
import { AuditStats, CompanyProfile, EdmAccountStatus, EdmProbeCompany, Settings as SettingsType } from "../types";
import { useSettings } from "../context/SettingsContext";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import "./Receivables.css";
import "./Settings.css";

/* ============================ tipler ============================ */

interface Form {
  settingsName: string; profitMargin: number; overheadCostRate: number; pricesIncludeVat: boolean;
  defaultVatRate: number; vatRates: string; faturasizSeriesCode: string; invoiceLineSuffix: string; defaultPaymentTermDays: number;
  notifyCustomerByEmail: boolean;
  publicBookingEnabled: boolean;
  auditRetentionDays: number;
}
type Profile = Omit<CompanyProfile, "companyProfileId"> & { companyProfileId?: string };
type Section = "pricing" | "invoicing" | "company" | "receivables" | "customer" | "booking" | "audit" | "license";
const SECTIONS: Section[] = ["pricing", "invoicing", "company", "receivables", "customer", "booking", "audit", "license"];
type FormErrors = Partial<Record<keyof Form, string>>;
type ProfileErrors = Partial<Record<keyof Profile, string>>;

const EMPTY_FORM: Form = {
  settingsName: "Varsayılan Ayarlar", profitMargin: 40, overheadCostRate: 40, pricesIncludeVat: false,
  defaultVatRate: 20, vatRates: "0,1,10,20", faturasizSeriesCode: "PRF", invoiceLineSuffix: "Nolu Baskı", defaultPaymentTermDays: 30,
  notifyCustomerByEmail: false, publicBookingEnabled: false, auditRetentionDays: 90,
};
const EMPTY_PROFILE: Profile = {
  taxNumber: "", title: "", taxOfficeName: "", streetName: "", buildingNumber: "", district: "", city: "",
  postalCode: "", countryCode: "TR", countryName: "Türkiye", phone: "", email: "", webSite: "", mersisNo: "", ticaretSicilNo: "",
};
const MODULE_DESC: Record<ModuleCode, string> = {
  invoicing: "Sistemden e-Fatura, e-Arşiv ve Faturasız belge kesimi, Kesilen Belgeler ekranı.",
  stock: "Stok, ürün ağacı; maliyet girişi ve üretim tamamlama stoktan düşer.",
  "dynamic-reports": "Pivot rapor tasarımcısı ve rapor şablonları.",
  kanban: "Sürükle-bırak atölye panosu.",
  receivables: "Firma bakiyeleri, tahsilat, vade ve yaşlandırma.",
  purchasing: "Tedarikçi siparişleri ve mal kabul; stoğa giriş yazar.",
  shipping: "Sevkiyat, sevk irsaliyesi ve teslimat takibi; stoktan çıkış yazar.",
  appointments: "Dışarıdan randevu alma sayfası ve çalışan takvimleri.",
  hr: "Personel kartları, departman ve pozisyonlar; izin ve giriş-çıkış takibinin temeli.",
  finance: "Kasa ve banka hesapları, kasa defteri, virman; tahsilatlar hesaba işlenir. Tedarikçi carisi, çek/senet ve mizan bu modülde gelecek.",
};

/* ============================ yardımcılar ============================ */

const parseRates = (csv: string) =>
  (csv || "").split(",").map((p) => parseFloat(p.trim().replace(",", "."))).filter((n) => !isNaN(n) && n >= 0 && n <= 100)
    .filter((n, i, a) => a.indexOf(n) === i).sort((a, b) => a - b);
const money = (n: number) => n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺";
const pct = (n: number) => `%${n.toLocaleString("tr-TR")}`;
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const toForm = (d: SettingsType): Form => ({
  settingsName: d.settingsName, profitMargin: d.profitMargin, overheadCostRate: d.overheadCostRate,
  pricesIncludeVat: d.pricesIncludeVat ?? false, defaultVatRate: d.defaultVatRate ?? 20,
  vatRates: d.vatRates || "0,1,10,20", faturasizSeriesCode: d.faturasizSeriesCode || "PRF",
  invoiceLineSuffix: d.invoiceLineSuffix ?? "Nolu Baskı",
  defaultPaymentTermDays: d.defaultPaymentTermDays ?? 30,
  notifyCustomerByEmail: d.notifyCustomerByEmail ?? false,
  publicBookingEnabled: d.publicBookingEnabled ?? false,
  auditRetentionDays: d.auditRetentionDays ?? 90,
});
const toProfile = (p: CompanyProfile): Profile => ({ ...EMPTY_PROFILE, ...Object.fromEntries(Object.entries(p).map(([k, v]) => [k, v ?? ""])) } as Profile);

const I = {
  pricing: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
  invoice: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3h9l4 4v14H6z" /><path d="M15 3v4h4M9 12h6M9 16h6" /></svg>,
  company: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-5h6v5M9 10h.01M15 10h.01M9 14h.01M15 14h.01" /></svg>,
  receivables: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="m7 14 3-4 3 3 5-7" /></svg>,
  license: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>,
  bell: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>,
  ext: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17 17 7M8 7h9v9" /></svg>,
  info: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>,
  shield: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-3.5 8-10V5l-8-3-8 3v7c0 6.5 8 10 8 10Z" /><path d="M9 9h6M9 12.5h6M9 16h3" /></svg>,
};
/** "2026-10-01T03:30:00" → "01.10.2026 03:30" (sunucu saati TR yerel; Date'e çevirmeden, kaydırmadan). */
const fmtTr = (s?: string | null) => { if (!s) return "—"; const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(s); if (!m) return s; return `${m[3]}.${m[2]}.${m[1]}${m[4] ? ` ${m[4]}:${m[5]}` : ""}`; };

/** Açıklama | kontrol satırı. Bileşen DIŞINDA tanımlı: içeride tanımlanınca her tuşta yeni bileşen türü oluşuyor,
 *  alt ağaç yeniden kuruluyor ve girişler odağı kaybediyordu. */
const Row: React.FC<{ label: string; desc?: React.ReactNode; req?: boolean; dirty?: boolean; children: React.ReactNode }> = ({ label, desc, req, dirty: d, children }) => (
  <div className={`st-row ${d ? "dirty" : ""}`}>
    <div><div className="st-row-label">{label}{req && <span className="req">*</span>}</div>{desc && <p className="st-row-desc">{desc}</p>}</div>
    <div className="st-row-ctl">{children}</div>
  </div>
);

/**
 * Sistem Ayarları — sol gezinme, sağda "açıklama | kontrol" satırlı bölümler.
 * İki ayrı kayıt (genel ayarlar, şirket profili) tek kaydet çubuğuyla yönetilir;
 * yalnız değişen kayıt gönderilir. Satır bazında "değişti" işareti düşer.
 */
const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { refreshSettings } = useSettings();
  const { license, hasModule, has } = usePermissions();
  const canEdit = has("settings:update");

  const [sp] = useSearchParams();
  const [section, setSection] = useState<Section>(() => { const s = sp.get("section") as Section; return SECTIONS.includes(s) ? s : "pricing"; });
  const [auditStats, setAuditStats] = useState<AuditStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState<SettingsType | null>(null);
  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [savedForm, setSavedForm] = useState<Form>(EMPTY_FORM);
  const [profile, setProfile] = useState<Profile>(EMPTY_PROFILE);
  const [savedProfile, setSavedProfile] = useState<Profile>(EMPTY_PROFILE);
  const [profileExists, setProfileExists] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [pErrors, setPErrors] = useState<ProfileErrors>({});
  const [newRate, setNewRate] = useState("");

  // Entegratör hesabı — kimlik bilgileri Nedaris'te SAKLANMAZ; form alanları
  // yalnız gönderim anına kadar bellekte durur, kaydedildikten sonra temizlenir.
  const [edm, setEdm] = useState<EdmAccountStatus | null>(null);
  const [edmUser, setEdmUser] = useState("");
  const [edmPass, setEdmPass] = useState("");
  const [edmVkn, setEdmVkn] = useState("");
  const [edmCompanies, setEdmCompanies] = useState<EdmProbeCompany[]>([]);
  const [edmBusy, setEdmBusy] = useState<"" | "probe" | "save">("");
  const [edmMsg, setEdmMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [edmShowPass, setEdmShowPass] = useState(false);
  const [alert, setAlert] = useState<{ isOpen: boolean; message: string; type: "info" | "success" | "warning" | "error" }>({ isOpen: false, message: "", type: "info" });
  const say = (message: string, type: "info" | "success" | "warning" | "error" = "info") => setAlert({ isOpen: true, message, type });

  /* ---------- veri ---------- */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await settingsService.getDefault();
      setCurrent(d); setForm(toForm(d)); setSavedForm(toForm(d));
    } catch { setCurrent(null); setForm(EMPTY_FORM); setSavedForm(EMPTY_FORM); }
    if (hasModule("invoicing")) {
      const p = await companyProfileService.get();
      const pf = p ? toProfile(p) : EMPTY_PROFILE;
      setProfile(pf); setSavedProfile(pf); setProfileExists(!!p);
      // Entegratör hesabı ayrı bir sistemde; ulaşılamazsa sayfa yine açılır
      try { setEdm(await companyProfileService.edmAccount()); } catch { setEdm(null); }
    }
    setErrors({}); setPErrors({});
    setLoading(false);
  }, [hasModule]);
  useEffect(() => { load(); }, [load]);
  // Denetim izi özeti yalnız o bölüm açılınca ve ekranı görme yetkisi varsa
  useEffect(() => {
    if (section !== "audit" || !has("audit-trail:view")) return;
    auditService.stats().then(setAuditStats).catch(() => setAuditStats(null));
  }, [section, has, savedForm.auditRetentionDays]);

  /* ---------- türevler ---------- */
  const rates = useMemo(() => parseRates(form.vatRates), [form.vatRates]);
  const formDirty = !same(form, savedForm);
  const profileDirty = !same(profile, savedProfile);
  const dirty = formDirty || profileDirty;
  const changed = (k: keyof Form) => !same(form[k], savedForm[k]);
  const pchanged = (...ks: (keyof Profile)[]) => ks.some((k) => !same(profile[k], savedProfile[k]));
  const setF = <K extends keyof Form>(k: K, v: Form[K]) => { setForm((p) => ({ ...p, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };
  const setP = <K extends keyof Profile>(k: K, v: Profile[K]) => { setProfile((p) => ({ ...p, [k]: v })); setPErrors((e) => ({ ...e, [k]: undefined })); };

  const base = 1000, overhead = (base * form.overheadCostRate) / 100, subtotal = base + overhead, profit = (subtotal * form.profitMargin) / 100, sale = subtotal + profit;

  /* ---------- doğrulama ---------- */
  const validate = (): Section | null => {
    const e: FormErrors = {};
    if (!(form.profitMargin >= 0 && form.profitMargin <= 100)) e.profitMargin = "0 ile 100 arasında olmalı.";
    if (!(form.overheadCostRate >= 0 && form.overheadCostRate <= 100)) e.overheadCostRate = "0 ile 100 arasında olmalı.";
    if (rates.length === 0) e.vatRates = "En az bir KDV oranı tanımlı olmalı.";
    else if (!rates.includes(form.defaultVatRate)) e.defaultVatRate = "Varsayılan oran, seçilebilir oranlar arasında olmalı.";
    if (!/^[A-Z]{2,8}$/.test(form.faturasizSeriesCode)) e.faturasizSeriesCode = "2 ile 8 büyük harf (A-Z); rakam ve Türkçe karakter olamaz.";
    if (!(form.defaultPaymentTermDays >= 0 && form.defaultPaymentTermDays <= 365)) e.defaultPaymentTermDays = "0 ile 365 arasında olmalı.";
    if (!(Number.isInteger(form.auditRetentionDays) && form.auditRetentionDays >= 30 && form.auditRetentionDays <= 730)) e.auditRetentionDays = "30 ile 730 gün arasında olmalı.";
    const pe: ProfileErrors = {};
    if (profileDirty || profileExists) {
      const vkn = (profile.taxNumber || "").trim();
      if (!/^\d{10}$|^\d{11}$/.test(vkn)) pe.taxNumber = "VKN 10 ya da TCKN 11 haneli rakam olmalı.";
      if (!profile.title?.trim()) pe.title = "Unvan zorunlu.";
      if (profile.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) pe.email = "Geçerli bir e-posta girin.";
    }
    setErrors(e); setPErrors(pe);
    const k = Object.keys(e)[0] as keyof Form | undefined;
    if (k) return k === "profitMargin" || k === "overheadCostRate" ? "pricing" : k === "defaultPaymentTermDays" ? "receivables" : k === "auditRetentionDays" ? "audit" : "invoicing";
    if (Object.keys(pe).length) return "company";
    return null;
  };

  const save = async () => {
    const bad = validate();
    if (bad) { setSection(bad); say("Eksik ya da hatalı alan var; ilgili bölüme geçildi.", "warning"); return; }
    setSaving(true);
    const done: string[] = [];
    try {
      if (formDirty) {
        const payload = { ...form, vatRates: rates.join(",") };
        if (current) await settingsService.update(current.settingsId, { ...current, ...payload });
        else await settingsService.create(payload as any);
        done.push("genel ayarlar");
      }
      if (profileDirty) {
        await companyProfileService.save({ ...profile, taxNumber: profile.taxNumber.trim(), countryCode: profile.countryCode || "TR", countryName: profile.countryName || "Türkiye" });
        done.push("şirket profili");
      }
      await load(); await refreshSettings();
      say(`Kaydedildi: ${done.join(" ve ")}.`, "success");
    } catch (err: any) {
      const d = err?.response?.data;
      say(typeof d === "string" ? d : d?.message || "Kaydedilemedi.", "error");
    } finally { setSaving(false); }
  };

  const discard = () => { setForm(savedForm); setProfile(savedProfile); setErrors({}); setPErrors({}); };

  /* ---------- entegratör hesabı ---------- */
  const edmErr = (e: any, fallback: string) => {
    const d = e?.response?.data;
    return [d?.title, d?.detail].filter(Boolean).join(" — ") || (typeof d === "string" ? d : fallback);
  };

  const probeEdm = async () => {
    if (!edmUser.trim() || !edmPass) return setEdmMsg({ ok: false, text: "Kullanıcı adı ve parola girin." });
    setEdmBusy("probe"); setEdmMsg(null); setEdmCompanies([]);
    try {
      const r = await companyProfileService.probeEdmAccount({ edmUserName: edmUser.trim(), edmPassword: edmPass });
      setEdmCompanies(r.companies || []);
      if ((r.companies?.length ?? 0) === 1) setEdmVkn(r.companies[0].taxNumber);
      const names = (r.companies || []).map((c) => `${c.taxNumber}${c.title ? ` (${c.title})` : ""}`).join(", ");
      setEdmMsg({ ok: true, text: `Bağlantı başarılı.${names ? ` Firma: ${names}.` : ""}${r.remainingCounter != null ? ` Kalan kontör: ${r.remainingCounter}.` : ""}` });
    } catch (e) { setEdmMsg({ ok: false, text: edmErr(e, "Bağlantı denenemedi.") }); }
    finally { setEdmBusy(""); }
  };

  const saveEdm = async () => {
    if (!edmUser.trim() || !edmPass) return setEdmMsg({ ok: false, text: "Kullanıcı adı ve parola girin." });
    setEdmBusy("save"); setEdmMsg(null);
    try {
      const r = await companyProfileService.saveEdmAccount({
        edmUserName: edmUser.trim(), edmPassword: edmPass,
        senderIdentifier: edmVkn.trim() || null,
      });
      setEdm(r); setEdmPass(""); setEdmUser(""); setEdmVkn(""); setEdmCompanies([]); setEdmShowPass(false);
      setEdmMsg({ ok: true, text: "Entegratör hesabı kaydedildi. Parola bu ekranda saklanmaz." });
    } catch (e) { setEdmMsg({ ok: false, text: edmErr(e, "Kaydedilemedi.") }); }
    finally { setEdmBusy(""); }
  };

  const addRate = () => {
    const n = parseFloat((newRate || "").replace(",", "."));
    if (isNaN(n) || n < 0 || n > 100) return;
    if (!rates.includes(n)) setF("vatRates", [...rates, n].sort((a, b) => a - b).join(","));
    setNewRate("");
  };

  if (loading) return <PageLoader message="Ayarlar yükleniyor..." />;

  const NAV: { key: Section; label: string; icon: React.ReactNode; show: boolean; tag?: string }[] = [
    { key: "pricing", label: "Fiyatlandırma", icon: I.pricing, show: true },
    { key: "invoicing", label: "Fatura ve KDV", icon: I.invoice, show: true },
    { key: "company", label: "Şirket Profili", icon: I.company, show: hasModule("invoicing"), tag: hasModule("invoicing") && !profileExists ? "eksik" : undefined },
    { key: "receivables", label: "Cari Hesap", icon: I.receivables, show: hasModule("receivables") },
    { key: "customer", label: "Müşteri Bildirimleri", icon: I.bell, show: true, tag: form.notifyCustomerByEmail ? undefined : "kapalı" },
    { key: "booking", label: "Randevu Sayfası", icon: I.bell, show: hasModule("appointments"), tag: form.publicBookingEnabled ? undefined : "kapalı" },
    { key: "audit", label: "Denetim İzi", icon: I.shield, show: true },
    { key: "license", label: "Lisans ve Modüller", icon: I.license, show: true },
  ];

  return (
    <div className="rc-page st-page">
      <div className="rc-head">
        <div>
          <h1>Sistem Ayarları</h1>
          <p>Fiyatlandırma, belge ve cari varsayılanları ile satıcı kimliği. Değişiklikler tüm kullanıcıları etkiler.</p>
        </div>
        <div className="rc-head-actions"><button className="rc-btn" onClick={load} disabled={saving || dirty} title={dirty ? "Önce kaydedin ya da geri alın" : undefined}>Yenile</button></div>
      </div>

      {!canEdit && <div className="st-note warn" style={{ marginTop: "1rem" }}>{I.info}<span>Ayarları görüntülüyorsunuz; değiştirmek için "Sistem Ayarları · Güncelle" yetkisi gerekir.</span></div>}

      <div className="st-layout">
        {/* ===== Sol gezinme ===== */}
        <nav className="st-nav" aria-label="Ayar bölümleri">
          <div className="st-nav-title">Bölümler</div>
          {NAV.filter((n) => n.show).map((n) => (
            <button key={n.key} className={`st-nav-item ${section === n.key ? "active" : ""}`} onClick={() => setSection(n.key)} aria-current={section === n.key ? "page" : undefined}>
              {n.icon}{n.label}{n.tag && <small>{n.tag}</small>}
            </button>
          ))}
          <div className="st-nav-sep" />
          <button className="st-nav-item link" onClick={() => navigate("/notifications")}>{I.bell}Bildirim tercihleri{I.ext}</button>
        </nav>

        {/* ===== İçerik ===== */}
        <div>
          {section === "pricing" && (
            <section className="st-section">
              <div className="st-section-head"><div><h2>Fiyatlandırma</h2><p>Model maliyeti ve teklif fiyatı bu iki orana göre hesaplanır: önce genel gider maliyete eklenir, kar marjı bulunan ara toplama uygulanır.</p></div></div>
              <Row label="Genel gider oranı" req dirty={changed("overheadCostRate")} desc="Kira, enerji, yönetim gibi dolaylı giderlerin doğrudan maliyete eklenme payı.">
                <div className="st-suffix st-w-sm"><input className={`st-input num ${errors.overheadCostRate ? "err" : ""}`} type="number" min={0} max={100} step={0.5} value={form.overheadCostRate} disabled={!canEdit} onChange={(e) => setF("overheadCostRate", parseFloat(e.target.value) || 0)} /><span>%</span></div>
                {errors.overheadCostRate && <p className="st-err">{errors.overheadCostRate}</p>}
              </Row>
              <Row label="Kar marjı" req dirty={changed("profitMargin")} desc="Genel gider eklendikten sonraki tutara uygulanır.">
                <div className="st-suffix st-w-sm"><input className={`st-input num ${errors.profitMargin ? "err" : ""}`} type="number" min={0} max={100} step={0.5} value={form.profitMargin} disabled={!canEdit} onChange={(e) => setF("profitMargin", parseFloat(e.target.value) || 0)} /><span>%</span></div>
                {errors.profitMargin && <p className="st-err">{errors.profitMargin}</p>}
              </Row>
              <Row label="Örnek hesap" desc="1.000 ₺ doğrudan maliyetli bir iş için, girdiğiniz oranlarla anlık hesap.">
                <div className="st-calc">
                  <div className="st-calc-head">Satış fiyatı nasıl oluşur</div>
                  <div className="st-calc-row"><span className="lbl">Doğrudan maliyet</span><span className="val">{money(base)}</span></div>
                  <div className="st-calc-row add"><span className="lbl">Genel gider ({pct(form.overheadCostRate)})</span><span className="val">+ {money(overhead)}</span></div>
                  <div className="st-calc-row"><span className="lbl">Ara toplam</span><span className="val">{money(subtotal)}</span></div>
                  <div className="st-calc-row add"><span className="lbl">Kar ({pct(form.profitMargin)})</span><span className="val">+ {money(profit)}</span></div>
                  <div className="st-calc-row total"><span className="lbl">Satış fiyatı, KDV {form.pricesIncludeVat ? "dahil" : "hariç"}</span><span className="val">{money(sale)}</span></div>
                </div>
                {!form.pricesIncludeVat && <p className="st-hint">Faturada üzerine {pct(form.defaultVatRate)} KDV eklenir: {money(sale * (1 + form.defaultVatRate / 100))}.</p>}
              </Row>
              <Row label="Ayar kaydı adı" dirty={changed("settingsName")} desc="Yalnız kayıt etiketi; hesaplamayı etkilemez.">
                <input className="st-input st-w-md" value={form.settingsName} maxLength={100} disabled={!canEdit} onChange={(e) => setF("settingsName", e.target.value)} />
              </Row>
            </section>
          )}

          {section === "invoicing" && (
            <section className="st-section">
              <div className="st-section-head">
                <div><h2>Fatura ve KDV</h2><p>Belge kesiminde kullanılan varsayımlar. KDV dahil ve hariç ayarı yanlışsa her fatura KDV oranı kadar sapar; kesmeden önce burayı doğrulayın.</p></div>
                {!hasModule("invoicing") && <span className="st-badge off">Fatura modülü kapalı</span>}
              </div>
              <Row label="Sipariş fiyatları KDV dahil" dirty={changed("pricesIncludeVat")} desc={form.pricesIncludeVat ? "Açık: fiyatlar KDV içerir kabul edilir, matrah geriye hesaplanır." : "Kapalı: KDV, sipariş fiyatının üstüne eklenir."}>
                <label className="st-switch-row"><span className="st-switch"><input type="checkbox" checked={form.pricesIncludeVat} disabled={!canEdit} onChange={(e) => setF("pricesIncludeVat", e.target.checked)} /><span /></span><span className="st-switch-text">{form.pricesIncludeVat ? "Dahil" : "Hariç"}</span></label>
              </Row>
              <Row label="Seçilebilir KDV oranları" req dirty={changed("vatRates")} desc="Fatura satırında bu listeden seçilir; sunucu da listeye karşı doğrular, liste dışı oran reddedilir. Bir orana tıklayarak varsayılan yapabilirsiniz.">
                <div className="st-chips">
                  {rates.map((r) => (
                    <span key={r} className={`st-chip ${r === form.defaultVatRate ? "default" : ""}`} onClick={() => canEdit && setF("defaultVatRate", r)} role="button" tabIndex={0} title="Varsayılan yap" onKeyDown={(e) => e.key === "Enter" && canEdit && setF("defaultVatRate", r)}>
                      {pct(r)}{r === form.defaultVatRate && <small>varsayılan</small>}
                      {canEdit && rates.length > 1 && <button type="button" onClick={(e) => { e.stopPropagation(); const next = rates.filter((x) => x !== r); setF("vatRates", next.join(",")); if (r === form.defaultVatRate) setF("defaultVatRate", next[0]); }} aria-label={`${pct(r)} oranını kaldır`}>{I.x}</button>}
                    </span>
                  ))}
                  {rates.length === 0 && <span className="st-hint" style={{ margin: 0 }}>Tanımlı oran yok.</span>}
                </div>
                {canEdit && (
                  <div className="st-chip-add">
                    <input className="st-input num" inputMode="decimal" placeholder="Oran" value={newRate} onChange={(e) => setNewRate(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRate(); } }} />
                    <button type="button" className="rc-btn sm" onClick={addRate}>Ekle</button>
                  </div>
                )}
                {(errors.vatRates || errors.defaultVatRate) && <p className="st-err">{errors.vatRates || errors.defaultVatRate}</p>}
              </Row>
              <Row label="Faturasız belge ön eki" req dirty={changed("faturasizSeriesCode")} desc="Faturasız (proforma) belge numarasının ön eki. Seri değişince sayaç birden başlar. e-Fatura serisi buradan yönetilmez; entegratör hesabından gelir.">
                <input className={`st-input mono st-w-sm ${errors.faturasizSeriesCode ? "err" : ""}`} value={form.faturasizSeriesCode} maxLength={8} disabled={!canEdit} onChange={(e) => setF("faturasizSeriesCode", e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))} />
                {errors.faturasizSeriesCode ? <p className="st-err">{errors.faturasizSeriesCode}</p> : <p className="st-hint">Örnek numara: <b>{(form.faturasizSeriesCode || "PRF")}{new Date().getFullYear()}000000001</b></p>}
              </Row>
              <Row label="Belge satırı metni" dirty={changed("invoiceLineSuffix")} desc="Fatura / faturasız belgede mal-hizmet satırı, model kodunun sağına bu metin eklenerek yazılır. Boş bırakılırsa yalnız model kodu yazılır. Belge keserken satır yine düzenlenebilir.">
                <input className="st-input st-w-md" value={form.invoiceLineSuffix} maxLength={100} disabled={!canEdit} placeholder="Nolu Baskı" onChange={(e) => setF("invoiceLineSuffix", e.target.value)} />
                <p className="st-hint">Örnek satır: <b>{["9025", form.invoiceLineSuffix.trim()].filter(Boolean).join(" ")}</b></p>
              </Row>
            </section>
          )}

          {section === "company" && hasModule("invoicing") && (
            <section className="st-section">
              <div className="st-section-head">
                <div><h2>Şirket Profili</h2><p>Kesilen her belgede satıcı olarak görünen kimlik. VKN, entegratör hesabındaki gönderici numarasıyla birebir aynı olmalı; aksi halde gönderim reddedilir.</p></div>
                <span className={`st-badge ${profileExists ? "ok" : "warn"}`}>{profileExists ? "Tanımlı" : "Tanımsız, belge kesilemez"}</span>
              </div>
              <Row label="Vergi kimliği" req dirty={pchanged("taxNumber", "taxOfficeName")} desc="Tüzel kişi için 10 haneli VKN, şahıs firması için 11 haneli TCKN.">
                <div className="st-fields">
                  <div><label>VKN / TCKN</label><input className={`st-input mono ${pErrors.taxNumber ? "err" : ""}`} inputMode="numeric" maxLength={11} value={profile.taxNumber} disabled={!canEdit} onChange={(e) => setP("taxNumber", e.target.value.replace(/\D/g, ""))} />{pErrors.taxNumber && <p className="st-err">{pErrors.taxNumber}</p>}</div>
                  <div><label>Vergi dairesi</label><input className="st-input" value={profile.taxOfficeName || ""} maxLength={100} disabled={!canEdit} onChange={(e) => setP("taxOfficeName", e.target.value)} /></div>
                </div>
              </Row>
              <Row label="Unvan" req dirty={pchanged("title")} desc="Ticaret sicilindeki tam unvan; belge başlığında olduğu gibi basılır.">
                <input className={`st-input ${pErrors.title ? "err" : ""}`} value={profile.title} maxLength={200} disabled={!canEdit} onChange={(e) => setP("title", e.target.value)} />
                {pErrors.title && <p className="st-err">{pErrors.title}</p>}
              </Row>
              <Row label="Adres" dirty={pchanged("streetName", "buildingNumber", "district", "city", "postalCode")} desc="e-Belge standardı adresi parçalı ister; cadde ve bina numarası ayrı alanlardır.">
                <div className="st-fields">
                  <div className="full"><label>Cadde / sokak</label><input className="st-input" value={profile.streetName || ""} maxLength={200} disabled={!canEdit} onChange={(e) => setP("streetName", e.target.value)} /></div>
                  <div><label>Bina no</label><input className="st-input" value={profile.buildingNumber || ""} maxLength={20} disabled={!canEdit} onChange={(e) => setP("buildingNumber", e.target.value)} /></div>
                  <div><label>Posta kodu</label><input className="st-input" value={profile.postalCode || ""} maxLength={10} disabled={!canEdit} onChange={(e) => setP("postalCode", e.target.value)} /></div>
                  <div><label>İlçe</label><input className="st-input" value={profile.district || ""} maxLength={100} disabled={!canEdit} onChange={(e) => setP("district", e.target.value)} /></div>
                  <div><label>İl</label><input className="st-input" value={profile.city || ""} maxLength={100} disabled={!canEdit} onChange={(e) => setP("city", e.target.value)} /></div>
                </div>
              </Row>
              <Row label="İletişim" dirty={pchanged("phone", "email", "webSite")} desc="Belge üstünde alıcıya gösterilir.">
                <div className="st-fields">
                  <div><label>Telefon</label><input className="st-input" value={profile.phone || ""} maxLength={30} disabled={!canEdit} onChange={(e) => setP("phone", e.target.value)} /></div>
                  <div><label>E-posta</label><input className={`st-input ${pErrors.email ? "err" : ""}`} type="email" value={profile.email || ""} maxLength={100} disabled={!canEdit} onChange={(e) => setP("email", e.target.value)} />{pErrors.email && <p className="st-err">{pErrors.email}</p>}</div>
                  <div className="full"><label>Web sitesi</label><input className="st-input" value={profile.webSite || ""} maxLength={100} disabled={!canEdit} onChange={(e) => setP("webSite", e.target.value)} /></div>
                </div>
              </Row>
              <Row label="Sicil bilgileri" dirty={pchanged("mersisNo", "ticaretSicilNo")} desc="İsteğe bağlı; girilirse belgeye işlenir.">
                <div className="st-fields">
                  <div><label>MERSİS no</label><input className="st-input mono" value={profile.mersisNo || ""} maxLength={16} disabled={!canEdit} onChange={(e) => setP("mersisNo", e.target.value.replace(/\D/g, ""))} /></div>
                  <div><label>Ticaret sicil no</label><input className="st-input" value={profile.ticaretSicilNo || ""} maxLength={20} disabled={!canEdit} onChange={(e) => setP("ticaretSicilNo", e.target.value)} /></div>
                </div>
              </Row>
            </section>
          )}

          {/* Entegratör hesabı: ayrı kart, çünkü ayrı bir sistemde saklanıyor ve
              üstteki Kaydet çubuğuna DAHİL DEĞİL — kendi düğmesiyle gönderilir. */}
          {section === "company" && hasModule("invoicing") && (
            <section className="st-section">
              <div className="st-section-head">
                <div><h2>Entegratör Bağlantısı</h2><p>EDM kullanıcı bilgileriniz. Belge kesimi bu hesapla yapılır; bilgiler Nedaris'te saklanmaz, e-belge servisine şifrelenerek iletilir.</p></div>
                <span className={`st-badge ${edm?.isConfigured ? (edm.isActive ? "ok" : "warn") : "warn"}`}>
                  {edm?.isConfigured ? (edm.isActive ? "Bağlı" : "Pasif") : "Tanımsız"}
                </span>
              </div>

              {edm?.isConfigured && (
                <Row label="Mevcut hesap" desc="Parola hiçbir zaman geri okunmaz; değiştirmek için aşağıya yenisini girin.">
                  <dl className="st-fields" style={{ margin: 0 }}>
                    <div><label>Kullanıcı adı</label><div className="st-input mono" style={{ display: "flex", alignItems: "center", background: "var(--st-bg)" }}>{edm.edmUserName}</div></div>
                    <div><label>Gönderici VKN</label><div className="st-input mono" style={{ display: "flex", alignItems: "center", background: "var(--st-bg)" }}>{edm.senderIdentifier}</div></div>
                    <div className="full"><p className="st-hint" style={{ margin: 0 }}>
                      {edm.displayName ? <>{edm.displayName} · </> : null}
                      Seri <b>{edm.defaultSerialCode}</b>
                      {edm.simulation ? " · SİMÜLASYON modunda" : ""}
                      {edm.updatedAt ? ` · son güncelleme ${new Date(edm.updatedAt).toLocaleString("tr-TR")}` : ""}
                    </p></div>
                  </dl>
                </Row>
              )}

              {profile.taxNumber && edm?.isConfigured && edm.senderIdentifier && edm.senderIdentifier !== profile.taxNumber && (
                <div style={{ padding: "0 1.5rem 0.5rem" }}>
                  <div className="st-note warn">{I.info}<span>Şirket profilindeki VKN ({profile.taxNumber}) ile entegratör hesabının gönderici VKN'si ({edm.senderIdentifier}) farklı. Fatura gönderimi bu haliyle reddedilir; ikisini eşitleyin.</span></div>
                </div>
              )}

              <Row label={edm?.isConfigured ? "Bilgileri güncelle" : "Kullanıcı bilgileri"} req={!edm?.isConfigured}
                desc="Entegratörün size verdiği kullanıcı adı ve parola. Kaydetmeden önce “Bağlantıyı sına” ile doğrulayın; yanlış bilgi ilk fatura denemesine kadar fark edilmez.">
                <div className="st-fields">
                  <div>
                    <label>EDM kullanıcı adı</label>
                    <input className="st-input mono" value={edmUser} maxLength={100} autoComplete="off" disabled={!canEdit || edmBusy !== ""}
                      placeholder={edm?.isConfigured ? edm.edmUserName || "" : ""} onChange={(e) => { setEdmUser(e.target.value); setEdmMsg(null); }} />
                  </div>
                  <div>
                    <label>Parola</label>
                    <div className="st-suffix">
                      <input className="st-input" type={edmShowPass ? "text" : "password"} value={edmPass} maxLength={200} autoComplete="new-password"
                        disabled={!canEdit || edmBusy !== ""} onChange={(e) => { setEdmPass(e.target.value); setEdmMsg(null); }} />
                      <span style={{ pointerEvents: "auto", cursor: "pointer" }} role="button" tabIndex={0}
                        onClick={() => setEdmShowPass((v) => !v)} onKeyDown={(e) => e.key === "Enter" && setEdmShowPass((v) => !v)}>
                        {edmShowPass ? "gizle" : "göster"}
                      </span>
                    </div>
                  </div>

                  {edmCompanies.length > 1 && (
                    <div className="full">
                      <label>Fatura kesilecek firma</label>
                      <select className="st-input" value={edmVkn} disabled={!canEdit} onChange={(e) => setEdmVkn(e.target.value)}>
                        <option value="">Seçin…</option>
                        {edmCompanies.map((c) => <option key={c.taxNumber} value={c.taxNumber}>{c.taxNumber}{c.title ? ` — ${c.title}` : ""}</option>)}
                      </select>
                      <p className="st-hint">Hesapta birden çok firma var; hangisi adına fatura kesileceğini seçin.</p>
                    </div>
                  )}

                  {canEdit && (
                    <div className="full" style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                      <button type="button" className="rc-btn" onClick={probeEdm} disabled={edmBusy !== "" || !edmUser.trim() || !edmPass}>
                        {edmBusy === "probe" ? "Deneniyor…" : "Bağlantıyı Sına"}
                      </button>
                      <button type="button" className="rc-btn primary" onClick={saveEdm} disabled={edmBusy !== "" || !edmUser.trim() || !edmPass || (edmCompanies.length > 1 && !edmVkn)}>
                        {edmBusy === "save" ? "Kaydediliyor…" : edm?.isConfigured ? "Bilgileri Güncelle" : "Hesabı Bağla"}
                      </button>
                    </div>
                  )}

                  {edmMsg && (
                    <div className="full">
                      <div className={`st-note ${edmMsg.ok ? "" : "warn"}`}>{I.info}<span>{edmMsg.text}</span></div>
                    </div>
                  )}
                </div>
              </Row>

              <div style={{ padding: "0 1.5rem 1.25rem" }}>
                <div className="st-note">{I.info}<span>Parola bu ekranda ve Nedaris veritabanında tutulmaz; e-belge servisine iletilip orada şifrelenerek saklanır. Kaydettikten sonra yalnız kullanıcı adı ve gönderici VKN geri okunabilir.</span></div>
              </div>
            </section>
          )}

          {section === "receivables" && hasModule("receivables") && (
            <section className="st-section">
              <div className="st-section-head"><div><h2>Cari Hesap</h2><p>Kesilen faturalar firma borcuna işlenirken kullanılan varsayılanlar.</p></div></div>
              <Row label="Fatura vadesi" req dirty={changed("defaultPaymentTermDays")} desc="Vadesi ayrıca girilmeyen borç kayıtlarında kullanılır; yaşlandırma ve vade bildirimleri bu tarihe göre çalışır.">
                <div className="st-suffix st-w-sm"><input className={`st-input num ${errors.defaultPaymentTermDays ? "err" : ""}`} type="number" min={0} max={365} value={form.defaultPaymentTermDays} disabled={!canEdit} onChange={(e) => setF("defaultPaymentTermDays", parseInt(e.target.value || "0", 10))} /><span>gün</span></div>
                {errors.defaultPaymentTermDays ? <p className="st-err">{errors.defaultPaymentTermDays}</p> : <p className="st-hint">Bugün kesilen bir faturanın vadesi: <b>{new Date(Date.now() + form.defaultPaymentTermDays * 86400000).toLocaleDateString("tr-TR")}</b></p>}
              </Row>
            </section>
          )}

          {section === "customer" && (
            <section className="st-section">
              <div className="st-section-head">
                <div><h2>Müşteri Bildirimleri</h2><p>Firmalara giden bilgilendirme postaları. Bu postalar şirket dışına, gerçek müşterilere gider; açmadan önce firma kartlarındaki bildirim adreslerinin doğru olduğundan emin olun.</p></div>
                <span className={`st-badge ${form.notifyCustomerByEmail ? "ok" : "off"}`}>{form.notifyCustomerByEmail ? "Açık" : "Kapalı"}</span>
              </div>
              <Row label="Müşteriye e-posta gönder" dirty={changed("notifyCustomerByEmail")}
                desc="Açıkken iki olayda posta gider: sipariş tamamlandığında ve sevkiyat yola çıktığında. Adres, firma kartındaki Bildirim E-postası alanından okunur; o alan boşsa o firmaya posta gitmez ve fatura adresine düşülmez.">
                <label className="st-switch-row">
                  <span className="st-switch">
                    <input type="checkbox" checked={form.notifyCustomerByEmail} disabled={!canEdit} onChange={(e) => setF("notifyCustomerByEmail", e.target.checked)} />
                    <span />
                  </span>
                  <span className="st-switch-text">{form.notifyCustomerByEmail ? "Gönderiliyor" : "Gönderilmiyor"}</span>
                </label>
                <p className="st-hint">Gönderim izi sistem günlüğüne yazılır; hangi firmaya ne zaman gittiği sonradan görülebilir.</p>
              </Row>
              <Row label="Gönderilen bildirimler" desc="Şimdilik bu iki olay destekleniyor.">
                <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: "0.86rem", lineHeight: 1.7, color: "var(--st-ink)" }}>
                  <li><b>Siparişiniz tamamlandı</b> — ürün, miktar ve tamamlanma tarihi.</li>
                  <li><b>Siparişiniz yola çıktı</b> — sevkiyat no, irsaliye no, nakliyeci, plaka, koli ve gönderilen ürünler.</li>
                </ul>
                <p className="st-hint">Postalarda fiyat ve tutar bilgisi yer almaz.</p>
              </Row>
            </section>
          )}

          {section === "booking" && (
            <section className="st-section">
              <div className="st-section-head">
                <div><h2>Randevu Sayfası</h2><p>Dışarıdaki kişilerin giriş yapmadan randevu alabildiği sayfa. Sistemin tanımadığı kişilerin kayıt yazabildiği tek yol burasıdır; varsayılan olarak kapalıdır.</p></div>
                <span className={`st-badge ${form.publicBookingEnabled ? "ok" : "off"}`}>{form.publicBookingEnabled ? "Yayında" : "Kapalı"}</span>
              </div>
              <Row label="Genel randevu sayfası yayında" dirty={changed("publicBookingEnabled")}
                desc="Kapalıyken hiçbir randevu adresi dışarıya açılmaz; modül yalnız iç takvim olarak çalışır. Açmak tek başına yetmez: her çalışanın kendi sayfasını Randevu ve Takvim ekranından ayrıca yayına alması gerekir.">
                <label className="st-switch-row">
                  <span className="st-switch">
                    <input type="checkbox" checked={form.publicBookingEnabled} disabled={!canEdit} onChange={(e) => setF("publicBookingEnabled", e.target.checked)} />
                    <span />
                  </span>
                  <span className="st-switch-text">{form.publicBookingEnabled ? "Açık" : "Kapalı"}</span>
                </label>
                <p className="st-hint">Dizin adresi: <code>/randevu</code> · Kişiye özel adres: <code>/randevu/&lt;bağlantı-adı&gt;</code></p>
              </Row>
              <Row label="Kötüye kullanıma karşı" desc="Sayfa herkese açık olduğu için bu önlemler her zaman devrededir.">
                <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: "0.86rem", lineHeight: 1.7, color: "var(--st-ink)" }}>
                  <li><b>E-posta doğrulaması</b> — talep, adres doğrulanana kadar kesinleşmez; doğrulanmazsa saat yeniden açılır.</li>
                  <li><b>Çalışan onayı</b> — doğrulanan her talep ilgili çalışanın onayını bekler.</li>
                  <li><b>Sınırlama</b> — aynı adresten günde, aynı bağlantıdan saatte belirli sayıda talep alınır.</li>
                  <li><b>Bilgi sızdırmaz</b> — dışarıya yalnız müsait saatler gider; çalışanın iletişim bilgisi ve doluluk sebebi gitmez.</li>
                </ul>
                <p className="st-hint">Misafir bilgileri randevu tarihinden bir yıl sonra otomatik silinir.</p>
              </Row>
            </section>
          )}

          {section === "audit" && (() => {
            const days = form.auditRetentionDays;
            const valid = Number.isInteger(days) && days >= 30 && days <= 730;
            const cut = new Date(); cut.setDate(cut.getDate() - (valid ? days : savedForm.auditRetentionDays));
            const shortening = valid && days < savedForm.auditRetentionDays;
            return (
              <section className="st-section">
                <div className="st-section-head">
                  <div><h2>Denetim İzi</h2><p>Her veri değişikliği, giriş ve önemli işlem kaydedilir. Kayıtlar salt okunurdur: düzenlenemez, tek tek silinemez; yalnızca saklama süresi dolunca otomatik temizlenir.</p></div>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                    <span className="st-badge ok">{savedForm.auditRetentionDays} gün saklama</span>
                    {has("audit-trail:view") && <button className="rc-btn" onClick={() => navigate("/audit-trail")}>Ekranı aç</button>}
                  </div>
                </div>
                <Row label="Saklama süresi" req dirty={changed("auditRetentionDays")}
                  desc="Bu süreden eski kayıtlar her gece 03:30'da otomatik silinir. Varsayılan 90 gün (3 ay). Yasal denetim ya da sözleşme gereği daha uzun tutmanız gerekiyorsa süreyi uzatın.">
                  <div className="st-suffix st-w-sm"><input className={`st-input num ${errors.auditRetentionDays ? "err" : ""}`} type="number" min={30} max={730} step={1} value={Number.isNaN(days) ? "" : days} disabled={!canEdit} onChange={(e) => setF("auditRetentionDays", parseInt(e.target.value || "0", 10))} /><span>gün</span></div>
                  {errors.auditRetentionDays ? <p className="st-err">{errors.auditRetentionDays}</p> : <p className="st-hint">Bugün itibarıyla <b>{cut.toLocaleDateString("tr-TR")}</b> tarihinden eski kayıtlar silinir. En az 30, en çok 730 gün.</p>}
                  {shortening && <div className="st-note warn" style={{ marginTop: "0.6rem" }}>{I.info}<span>Süre kısaltılıyor. Kaydettikten sonraki ilk gece temizliğinde daha eski kayıtlar kalıcı olarak silinir; bu işlem geri alınamaz.</span></div>}
                </Row>
                {has("audit-trail:view") && (
                  <Row label="Temizlik durumu" desc="Temizlik her gece 03:30'da çalışır; kayıtları 4.000'lik paketler halinde siler, kullanıcıları bekletmez. Elle silme yoktur; eşik tarihi yalnız saklama süresinden türer.">
                    {auditStats ? (
                      <div className="st-calc">
                        <div className="st-calc-head">Denetim izi tablosu</div>
                        <div className="st-calc-row"><span className="lbl">Toplam kayıt</span><span className="val">{auditStats.tableRows.toLocaleString("tr-TR")}</span></div>
                        <div className="st-calc-row"><span className="lbl">En eski kayıt</span><span className="val">{fmtTr(auditStats.oldestRecord)}</span></div>
                        <div className="st-calc-row"><span className="lbl">Son temizlik</span><span className="val">{auditStats.lastPurgeAt ? `${fmtTr(auditStats.lastPurgeAt)}${auditStats.lastPurgeDeleted != null ? ` · ${auditStats.lastPurgeDeleted.toLocaleString("tr-TR")} kayıt silindi` : ""}` : "henüz çalışmadı"}</span></div>
                        <div className="st-calc-row total"><span className="lbl">Sonraki çalışma</span><span className="val">{fmtTr(auditStats.nextPurgeAt)}</span></div>
                      </div>
                    ) : <p className="st-hint">Özet yüklenemedi.</p>}
                    {auditStats && !auditStats.enabled && <div className="st-note warn" style={{ marginTop: "0.6rem" }}>{I.info}<span>Denetim izi tablosu bulunamadı; veritabanı güncellemesi uygulanana kadar kayıt tutulmuyor.</span></div>}
                  </Row>
                )}
                <Row label="Kişisel verilerin korunması" desc="Bu kurallar her zaman devrededir; ayarla kapatılamaz.">
                  <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: "0.86rem", lineHeight: 1.7, color: "var(--st-ink)" }}>
                    <li><b>Parola ve erişim anahtarları</b>: değeri hiçbir zaman yazılmaz; yalnız değiştiği bilinir.</li>
                    <li><b>TC kimlik, sağlık notu, sürücü TCKN</b>: maskelenir. IBAN yalnız ilk ve son 4 hanesiyle görünür.</li>
                    <li><b>Bildirimler, döviz kurları, oturum belirteçleri, uygulama günlüğü</b>: izlenmez.</li>
                    <li><b>Başkasının özel personel verisini açmak</b>, dışa aktarımlar ve personel dosyası indirmeleri ayrıca kaydedilir.</li>
                  </ul>
                  <p className="st-hint">Kayıt düzenleme ve tek tek silme yolu yoktur; veritabanı da güncellemeyi ve elle silmeyi reddeder.</p>
                </Row>
              </section>
            );
          })()}

          {section === "license" && (
            <section className="st-section">
              <div className="st-section-head"><div><h2>Lisans ve Modüller</h2><p>Modüller imzalı lisans anahtarıyla belirlenir; bu ekrandan değiştirilemez. Yeni anahtar sunucu ayarlarındaki License:Key alanına işlenir ve uygulama yeniden başlatılır.</p></div></div>
              <div className="st-license">
                <div>
                  <div className="st-license-k">Lisans sahibi</div>
                  <div className="st-license-name">{license?.customer || "Lisans yok"}</div>
                  <div className="st-license-meta">{license?.expiresAt ? `Bitiş: ${new Date(license.expiresAt).toLocaleDateString("tr-TR")}` : "Süresiz lisans"} · {(Object.keys(MODULE_NAMES) as ModuleCode[]).filter(hasModule).length} / {Object.keys(MODULE_NAMES).length} modül açık</div>
                </div>
                <span className={`st-license-state ${license?.isValid ? "" : "off"}`}>{license?.status || "Lisans yok"}</span>
              </div>
              <ul className="st-mods">
                {(Object.keys(MODULE_NAMES) as ModuleCode[]).map((code) => (
                  <li key={code} className={`st-mod ${hasModule(code) ? "on" : "off"}`}>
                    <i /><div><b>{MODULE_NAMES[code]}</b><small>{MODULE_DESC[code]}</small></div>
                    <span className={`st-badge ${hasModule(code) ? "ok" : "off"}`}>{hasModule(code) ? "Açık" : "Lisansta yok"}</span>
                  </li>
                ))}
              </ul>
              <div style={{ padding: "1rem 1.5rem" }}><div className="st-note">{I.info}<span>Modül eklemek ya da lisansı yenilemek için Softana ile iletişime geçin. Kapalı modülün verisi silinmez; modül açılınca ekranlar ve izinler geri gelir.</span></div></div>
            </section>
          )}
        </div>
      </div>

      {/* ===== Yapışkan kaydet ===== */}
      {canEdit && (
        <div className={`st-bar ${dirty ? "show" : ""}`} aria-hidden={!dirty}>
          <div className="st-bar-inner">
            <div className="st-bar-text"><b>Kaydedilmemiş değişiklikler var</b>{[formDirty && "genel ayarlar", profileDirty && "şirket profili"].filter(Boolean).join(" ve ")} kaydedilecek. Kaydedene kadar eski değerler geçerli.</div>
            <div className="st-bar-btns">
              <button className="rc-btn" onClick={discard} disabled={saving}>Geri Al</button>
              <button className="rc-btn primary" onClick={save} disabled={saving}>{saving ? "Kaydediliyor…" : "Kaydet"}</button>
            </div>
          </div>
        </div>
      )}

      <AlertModal isOpen={alert.isOpen} message={alert.message} type={alert.type} onClose={() => setAlert({ ...alert, isOpen: false })} />
    </div>
  );
};

export default Settings;
